// quotes-slider.js
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);

import Flickity from "flickity";

class QuotesSlider {
    constructor(elementOrSelector) {
        this.root = typeof elementOrSelector === "string"
            ? document.querySelector(elementOrSelector)
            : elementOrSelector;

        if (!this.root) {
            console.warn("QuotesSlider: root element not found.", elementOrSelector);
            return;
        }

        this.carouselEl = this.root.querySelector(".quotes-slider");
        if (!this.carouselEl) {
            console.warn("QuotesSlider: .quotes-slider not found within root.", this.root);
            return;
        }

        this.nextArrow = this.root.querySelector(".js-slider-button-next");
        this.prevArrow = this.root.querySelector(".js-slider-button-prev");

        if (!this.nextArrow || !this.prevArrow) {
            console.warn("QuotesSlider: missing prev/next arrow buttons.", this.root);
        }

        this.flkty = new Flickity(this.carouselEl, {
            adaptiveHeight: false,
            cellAlign: "left",
            draggable: true,
            pageDots: false,
            prevNextButtons: false,
            initialIndex: 0,
            wrapAround: true,
        });

        this.slideCount = 0;

        this.flkty.on("ready", () => {
            this.slideCount = this.flkty.slides.length;

            // Höhe der Slides anpassen
            this.equalizeHeights();

            this.setCurrentSlide(this.flkty.selectedIndex);
            this.updatePassedSlides();
            ScrollTrigger.refresh();
        });

        this.flkty.on("settle", () => ScrollTrigger.refresh());

        this.flkty.on("change", () => this.onSlideChange());

        this.prevArrow?.addEventListener("click", (event) => {
            event.preventDefault();
            this.flkty.previous();
        });

        this.nextArrow?.addEventListener("click", (event) => {
            event.preventDefault();
            this.flkty.next();
        });

        const init = () => this.refresh();

        if (document.readyState === "complete") init();
        else window.addEventListener("load", init);

        // Bei Resize neu equalizen + is-passed neu setzen
        window.addEventListener("resize", () => {
            this.equalizeHeights();
            this.updatePassedSlides();
        });
    }

    onSlideChange() {
        this.setCurrentSlide(this.flkty.selectedIndex);
        this.updatePassedSlides();
    }

    /**
     * Setzt is-passed für alle Slides, die geometrisch "links" vom aktiven Slide liegen.
     */
    updatePassedSlides() {
        if (!this.flkty?.cells || this.flkty.cells.length === 0) return;

        const selectedIndex = this.flkty.selectedIndex;
        const activeCell = this.flkty.cells[selectedIndex];
        if (!activeCell) return;

        const activeEl = activeCell.element;
        const activeRect = activeEl.getBoundingClientRect();

        // Falls Layout noch nicht fertig ist
        if (activeRect.width === 0) {
            this.flkty.cells.forEach((cell) => {
                cell.element.classList.remove("is-passed");
            });
            return;
        }

        const activeCenterX = activeRect.left + activeRect.width / 2;

        this.flkty.cells.forEach((cell) => {
            const el = cell.element;
            const rect = el.getBoundingClientRect();
            const centerX = rect.left + rect.width / 2;

            const isLeftOfActive = centerX < activeCenterX - 1; // kleine Toleranz

            if (isLeftOfActive) {
                el.classList.add("is-passed");
            } else {
                el.classList.remove("is-passed");
            }
        });
    }

    setCurrentSlide() {
        if (!this.prevArrow || !this.nextArrow) return;

        // Im Loop-Modus nie disabled darstellen
        this.prevArrow.classList.remove("is-disabled");
        this.nextArrow.classList.remove("is-disabled");
    }

    refresh() {
        if (!this.flkty) return;

        this.flkty.reloadCells();
        this.flkty.resize();
        this.flkty.reposition();
        this.slideCount = this.flkty.slides.length;

        // Wichtig: danach neu equalizen
        this.equalizeHeights();

        this.setCurrentSlide(this.flkty.selectedIndex);
        this.updatePassedSlides();
    }

    /**
     * Alle Slides auf die gleiche (maximale) Höhe bringen
     */
    equalizeHeights() {
        if (!this.flkty?.cells || this.flkty.cells.length === 0) return;

        // 1. zurücksetzen
        this.flkty.cells.forEach((cell) => {
            const el = cell.element;
            el.style.height = "auto";
        });

        // 2. Maximalhöhe bestimmen
        let maxHeight = 0;
        this.flkty.cells.forEach((cell) => {
            const el = cell.element;
            const h = el.offsetHeight;
            if (h > maxHeight) maxHeight = h;
        });

        // 3. Maximalhöhe anwenden
        this.flkty.cells.forEach((cell) => {
            const el = cell.element;
            el.style.height = `${maxHeight}px`;
        });

        // 4. Flickity über geänderte Höhe informieren
        this.flkty.resize();
    }

    static mountAll(selector = ".js-quotes-slider") {
        document.querySelectorAll(selector).forEach((el) => new QuotesSlider(el));
    }
}

export default QuotesSlider;
